import { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import {
  SaveClientFlags,
  SetupOrgOnboardingSteps,
  UpdateCurrentOrg,
  UpdateCurrentUser,
} from '@sdk/authentication/types';
import { PositionType } from '@sdk/positions/types/PositionType';
import { LocationType } from '@sdk/locations/types/LocationType';
import { AuthProvider } from '@sdk/authentication/AuthContext';
import SideImageScreen from '@sdk/login/components/SideImageScreen';
import Spacing from '@sdk/components/ui/spacing/Spacing';
import StyledSvg from '@sdk/components/ui/StyledSvg';
import laptop from '@sdk/assets/images/web-login/signup-laptop.png';

import { PointOfSaleStep } from '@web/onboarding/steps/components/PointOfSaleStep';
import { useOnboarding } from '@web/onboarding/hooks/useOnboarding';
import { StyledSideImage } from '@web/onboarding/styles';
import FullPageContainer from '@web/components/FullPageContainer';
import { EmployeesStep } from '@web/onboarding/steps/components/EmployeesStep';
import { PositionsStep } from '@web/onboarding/steps/components/PositionsStep';
import { AccountStep } from '@web/onboarding/steps/components/AccountStep';
import ToastImg from '@web/assets/images/toast-integration.svg';
import { ToastConnectStep } from '@web/onboarding/steps/components/ToastConnectStep';
import { ToastAssistStep } from '@web/onboarding/steps/components/ToastAssistStep';
import { AccountStepWrapper } from '@web/onboarding/steps/components/AccountStepWrapper';
import { NumberedStepsWrapper } from '@web/onboarding/steps/components/NumberedStepsWrapper';

const getSideImage = (step): string | JSX.Element => {
  if (step === SetupOrgOnboardingSteps.toastConnect) {
    return (
      <Spacing mLeft={147}>
        <StyledSvg svg={ToastImg} width={374} height={247} viewBox="0 0 283 191" />
      </Spacing>
    );
  }

  return <StyledSideImage src={laptop} />;
};

type Props = {
  user: AuthUserType;
  locations: LocationType[];
  positions: PositionType[];
  saveClientFlags: SaveClientFlags;
  updateCurrentOrg: UpdateCurrentOrg;
  updateCurrentUser: UpdateCurrentUser;
  setFirstRun: () => void;
  loadPersonaCollection: () => Promise<void>;
  addPersonaCollectionGroup: (group: { group: any }) => void;
  addPersonaCollectionUser: (user: { user: any }) => void;
  onClose: () => void;
  toastIntegration: any;
};

export const Onboarding = ({
  user,
  locations,
  saveClientFlags,
  updateCurrentOrg,
  updateCurrentUser,
  setFirstRun,
  loadPersonaCollection,
  addPersonaCollectionGroup,
  addPersonaCollectionUser,
  positions,
  onClose,
  toastIntegration,
}: Props) => {
  const {
    steps,
    activeStep,
    pointOfSale,
    setPointOfSale,
    nextStep,
    orgSetupNextStep,
    onPointOfSaleSubmit,
    onPointOfSaleSkip,
    maybeCreateFirstLocation,
    close,
  } = useOnboarding({
    user,
    locations,
    onClose,
    saveClientFlags,
    loadPersonaCollection,
    addPersonaCollectionGroup,
    toastIntegration,
  });

  const handleManualSetup = (): void =>
    user.isUnverified ? close() : nextStep(SetupOrgOnboardingSteps.positions);

  const stepsMap = {
    [SetupOrgOnboardingSteps.account]: (
      <AccountStepWrapper steps={steps} activeStep={activeStep}>
        <AccountStep
          saveClientFlags={saveClientFlags}
          updateCurrentOrg={updateCurrentOrg}
          updateCurrentUser={updateCurrentUser}
          setFirstRun={setFirstRun}
          nextStep={orgSetupNextStep}
          signupToast={user.org.isToastSignup}
        />
      </AccountStepWrapper>
    ),
    [SetupOrgOnboardingSteps.toastAssist]: (
      <FullPageContainer>
        <SideImageScreen image={getSideImage(activeStep)}>
          <ToastAssistStep
            onManualSetup={handleManualSetup}
            onToastConnect={() => nextStep(SetupOrgOnboardingSteps.toastConnect)}
          />
        </SideImageScreen>
      </FullPageContainer>
    ),
    [SetupOrgOnboardingSteps.toastConnect]: (
      <FullPageContainer>
        <SideImageScreen image={getSideImage(activeStep)}>
          <ToastConnectStep back={(): void => nextStep(SetupOrgOnboardingSteps.toastAssist)} />
        </SideImageScreen>
      </FullPageContainer>
    ),
    [SetupOrgOnboardingSteps.pos]: (
      <PointOfSaleStep
        selected={pointOfSale}
        onSelect={setPointOfSale}
        onSkip={onPointOfSaleSkip}
        onSubmit={onPointOfSaleSubmit}
      />
    ),
    [SetupOrgOnboardingSteps.positions]: (
      <NumberedStepsWrapper steps={steps} activeStep={activeStep}>
        <PositionsStep
          onSuccess={() => nextStep(SetupOrgOnboardingSteps.employees)}
          onSkip={() => nextStep(SetupOrgOnboardingSteps.employees)}
          addPersonaCollectionGroup={addPersonaCollectionGroup}
        />
      </NumberedStepsWrapper>
    ),
    [SetupOrgOnboardingSteps.employees]: (
      <NumberedStepsWrapper steps={steps} activeStep={activeStep}>
        <EmployeesStep
          positions={positions}
          maybeCreateFirstLocation={maybeCreateFirstLocation}
          onSuccess={() => close()}
          onSkip={() => close()}
          addPersonaCollectionUser={addPersonaCollectionUser}
        />
      </NumberedStepsWrapper>
    ),
  };

  return <AuthProvider currentUser={user}>{stepsMap[activeStep]}</AuthProvider>;
};
